import React, { useState } from "react";
import { Formik } from "formik";
import { useLazyQuery } from "@apollo/client";
import { adminSearchInterests, adminSearchInterestsVariables } from "types/gql-types";

import { usePersistedSearch } from "../users/use-persisted-search";
import ResultTable from "./result-table";
import InterestSearchForm from "./form";
import { SEARCH_QUERY } from "./queries";

const initialData = {
  firstName: "",
  lastName: "",
  email: "",
  strict: true,
  includeIncomplete: false,
};

const EMPTY_SEARCH_MESSAGE =
  "Enter a first name, last name, or email to see search results.";

const hasSearchCriteria = ({
  firstName,
  lastName,
  email,
}: Pick<adminSearchInterestsVariables, "firstName" | "lastName" | "email">) =>
  Boolean(firstName?.trim() || lastName?.trim() || email?.trim());

const normalizeSearchVariables = (
  variables: adminSearchInterestsVariables,
): adminSearchInterestsVariables => ({
  email: variables.email?.trim() ?? "",
  firstName: variables.firstName?.trim() ?? "",
  lastName: variables.lastName?.trim() ?? "",
  strict: Boolean(variables.strict),
  includeIncomplete: Boolean(variables.includeIncomplete),
});

const InterestPage: React.FC = () => {
  const [hasSearched, setHasSearched] = useState(false);
  const [searchCriteria, setSearchCriteria] =
    useState<adminSearchInterestsVariables>(initialData);

  const [searchFunction, searchQueryData] = useLazyQuery<adminSearchInterests,
  adminSearchInterestsVariables>(
    SEARCH_QUERY,
    { variables: normalizeSearchVariables(searchCriteria), fetchPolicy: "network-only" },
  );

  const [initialFormValues, searchSubmit] = usePersistedSearch(initialData, (variables) => {
    const normalized = normalizeSearchVariables(variables);
    setHasSearched(true);
    setSearchCriteria(normalized);
    if (!hasSearchCriteria(normalized)) {
      return;
    }
    searchFunction({ variables: normalized });
  });

  return <>
    <Formik initialValues={initialFormValues} onSubmit={searchSubmit}>
      {(state) => (<InterestSearchForm {...state} />)}
    </Formik>

    <hr />

    {hasSearched && !hasSearchCriteria(searchCriteria) && (
      <div id="admin-interest-search-empty-criteria">{EMPTY_SEARCH_MESSAGE}</div>
    )}
    {hasSearched && hasSearchCriteria(searchCriteria) && (
      <ResultTable {...searchCriteria} searchQueryData={searchQueryData} />
    )}

    <hr />
  </>;
};

export default InterestPage;
